import React, { useState, useEffect } from 'react';
import './BulkUpload.css';

const BulkUploadManager = ({ onBack }) => {
  // State management
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

  // Load subjects on mount
  useEffect(() => {
    loadSubjects();
  }, []);

  // Load chapters when subject changes
  useEffect(() => {
    if (selectedSubject) {
      loadChapters(selectedSubject);
      setSelectedChapter('');
      setSelectedTopic('');
    }
  }, [selectedSubject]);

  // Load topics when chapter changes
  useEffect(() => {
    if (selectedChapter && selectedSubject) {
      loadTopics(selectedChapter, selectedSubject);
      setSelectedTopic('');
    }
  }, [selectedChapter]);

  // API Calls
  const loadSubjects = async () => {
    try {
      const response = await fetch(`${API_BASE}/subjects/`);
      const data = await response.json();
      setSubjects(data);
    } catch (err) {
      setError('Failed to load subjects');
      console.error(err);
    }
  };

  const loadChapters = async (subjectId) => {
    try {
      const response = await fetch(`${API_BASE}/chapters/?subject_id=${subjectId}`);
      const data = await response.json();
      setChapters(data);
    } catch (err) {
      setError('Failed to load chapters');
      console.error(err);
    }
  };

  const loadTopics = async (chapterId, subjectId) => {
    try {
      const response = await fetch(`${API_BASE}/topics/?chapter_id=${chapterId}&subject_id=${subjectId}`);
      const data = await response.json();
      setTopics(data);
    } catch (err) {
      setError('Failed to load topics');
      console.error(err);
    }
  };

  // Download template with existing data
  const handleDownloadTemplate = async () => {
    if (!selectedSubject || !selectedChapter || !selectedTopic) {
      setError('Please select Subject, Chapter, and Topic first');
      return;
    }

    setDownloadingTemplate(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/excel-bulk-upload/download-template/${selectedTopic}?subject_id=${selectedSubject}&chapter_id=${selectedChapter}`
      );

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'bulk_upload_template.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('Template downloaded successfully');
    } catch (err) {
      setError('Failed to download template: ' + err.message);
      console.error(err);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx or .xls)');
        setUploadFile(null);
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        setUploadFile(null);
        return;
      }

      setUploadFile(file);
      setError(null);
      setUploadResults(null);
    }
  };

  // Upload and process file
  const handleUpload = async () => {
    if (!uploadFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResults(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await fetch(`${API_BASE}/excel-bulk-upload/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }

      setUploadResults(data);
      
      // Clear file input on success
      if (data.success) {
        setUploadFile(null);
        document.getElementById('file-input').value = '';
      }

    } catch (err) {
      setError('Upload failed: ' + err.message);
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedSubject('');
    setSelectedChapter('');
    setSelectedTopic('');
    setUploadFile(null);
    setUploadResults(null);
    setError(null);
  };

  

  return (
    <div className="bulk-upload-container">
      {/* Add Back Button */}
      {onBack && (
        <div className="management-header">
          <button className="back-btn" onClick={onBack}>
            ← Back to Dashboard
          </button>
        </div>
      )}

      <div className="bulk-upload-header">
        <h1>📊 Excel Bulk Upload</h1>
        <p>Upload chunks, micro-chunks, and explanations in bulk using Excel</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">❌</span>
          {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Selection Panel */}
      <div className="selection-panel">
        <h3>Step 1: Select Topic</h3>
        
        <div className="selection-row">
          {/* Subject */}
          <div className="selection-item">
            <label>📚 Subject</label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="">-- Select Subject --</option>
              {subjects.map(subject => (
                <option key={subject.subject_id} value={subject.subject_id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>

          {/* Chapter */}
          <div className="selection-item">
            <label>📖 Chapter</label>
            <select
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e.target.value)}
              disabled={!selectedSubject}
            >
              <option value="">-- Select Chapter --</option>
              {chapters.map(chapter => (
                <option key={chapter.chapter_id} value={chapter.chapter_id}>
                  {chapter.name}
                </option>
              ))}
            </select>
          </div>

          {/* Topic */}
          <div className="selection-item">
            <label>📝 Topic</label>
            <select
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
              disabled={!selectedChapter}
            >
              <option value="">-- Select Topic --</option>
              {topics.map(topic => (
                <option key={topic.topic_id} value={topic.topic_id}>
                  {topic.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Button */}
          <div className="selection-item">
            <label>&nbsp;</label>
            <button 
              className="btn btn-secondary"
              onClick={handleClearSelection}
              disabled={!selectedSubject}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Download Template Section */}
      <div className="action-panel">
        <h3>Step 2: Download Template</h3>
        <p>Download Excel template with existing data for the selected topic</p>
        
        <button
          className="btn btn-primary btn-large"
          onClick={handleDownloadTemplate}
          disabled={!selectedTopic || downloadingTemplate}
        >
          {downloadingTemplate ? (
            <>⏳ Downloading...</>
          ) : (
            <>📥 Download Excel Template</>
          )}
        </button>
      </div>

      {/* Upload Section */}
      <div className="action-panel">
        <h3>Step 3: Upload Filled Excel</h3>
        <p>Upload the Excel file after filling/updating data</p>
        
        <div className="upload-section">
          <input
            id="file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="file-input"
          />
          
          {uploadFile && (
            <div className="file-selected">
              <span className="file-icon">📄</span>
              <span className="file-name">{uploadFile.name}</span>
              <span className="file-size">({(uploadFile.size / 1024).toFixed(2)} KB)</span>
            </div>
          )}

          <button
            className="btn btn-success btn-large"
            onClick={handleUpload}
            disabled={!uploadFile || uploading}
          >
            {uploading ? (
              <>⏳ Processing...</>
            ) : (
              <>📤 Upload and Process</>
            )}
          </button>
        </div>
      </div>

      {/* Upload Results */}
      {uploadResults && (
        <div className={`results-panel ${uploadResults.success ? 'success' : 'error'}`}>
          <h3>
            {uploadResults.success ? '✅ Upload Successful' : '❌ Upload Failed'}
          </h3>

          {uploadResults.summary && (
            <div className="results-summary">
              <div className="summary-item">
                <span className="summary-label">Total Created:</span>
                <span className="summary-value">{uploadResults.summary.total_created}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Updated:</span>
                <span className="summary-value">{uploadResults.summary.total_updated}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Errors:</span>
                <span className="summary-value error">{uploadResults.summary.total_errors}</span>
              </div>
            </div>
          )}

          {/* Detailed Results */}
          <div className="results-details">
            {/* Chunks */}
            {uploadResults.chunks && (
              <div className="result-section">
                <h4>📄 Chunks</h4>
                <p>Created: {uploadResults.chunks.created} | Updated: {uploadResults.chunks.updated}</p>
                {uploadResults.chunks.errors && uploadResults.chunks.errors.length > 0 && (
                  <div className="errors-list">
                    <strong>Errors:</strong>
                    <ul>
                      {uploadResults.chunks.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Micro-Chunks */}
            {uploadResults.micro_chunks && (
              <div className="result-section">
                <h4>🔍 Micro-Chunks</h4>
                <p>Created: {uploadResults.micro_chunks.created} | Updated: {uploadResults.micro_chunks.updated}</p>
                {uploadResults.micro_chunks.errors && uploadResults.micro_chunks.errors.length > 0 && (
                  <div className="errors-list">
                    <strong>Errors:</strong>
                    <ul>
                      {uploadResults.micro_chunks.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Explanations */}
            {uploadResults.explanations && (
              <div className="result-section">
                <h4>💡 Explanations</h4>
                <p>Created: {uploadResults.explanations.created} | Updated: {uploadResults.explanations.updated}</p>
                {uploadResults.explanations.errors && uploadResults.explanations.errors.length > 0 && (
                  <div className="errors-list">
                    <strong>Errors:</strong>
                    <ul>
                      {uploadResults.explanations.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="instructions-panel">
        <h3>📋 Instructions</h3>
        <ol>
          <li>Select Subject, Chapter, and Topic from dropdowns</li>
          <li>Click "Download Excel Template" to get existing data</li>
          <li>Open Excel file and fill/update data as needed</li>
          <li>Leave ID blank to CREATE new item</li>
          <li>Fill ID to UPDATE existing item</li>
          <li>Leave description blank to keep existing data unchanged</li>
          <li>Upload the filled Excel file</li>
          <li>Review processing results</li>
        </ol>
      </div>
    </div>
  );
};

export default BulkUploadManager;